import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';
import { getPeriod, HISTORICAL_KEY_ALIASES, PREDICTION_KEY_ALIASES } from './config';
import { isMissing } from './format';

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

const NO_ERRORS = { marketData: null, historicalData: null, predictions: null };

// First non-null/undefined wins. Deliberately not a `||` chain: `||` would skip
// a legitimate 0 and fall through to the next candidate.
const firstPresent = (...values) => {
  for (const value of values) {
    if (value !== null && value !== undefined) return value;
  }
  return null;
};

// The comprehensive endpoint has returned both flat and nested shapes over time,
// so each field checks the flat key first and then the nested location. A field
// that exists in neither stays null - it is never backfilled with a constant.
const flattenMarketResponse = (response) => {
  const d = response.data;

  return {
    ...response,
    data: {
      fed_funds_rate: firstPresent(d.fed_funds_rate, d.rates?.fed_funds),
      treasury_10y: firstPresent(d.treasury_10y, d.rates?.treasury_10yr),
      treasury_2y: firstPresent(d.treasury_2y, d.rates?.treasury_2yr),
      treasury_5y: firstPresent(d.treasury_5y, d.rates?.treasury_5yr),
      mortgage_rate_30y: firstPresent(d.mortgage_rate_30y, d.rates?.mortgage_30yr),

      cpi_inflation: firstPresent(d.cpi_inflation, d.economic_indicators?.inflation_estimate, d.economic_indicators?.cpi_latest),
      inflation_rate: firstPresent(d.inflation_rate, d.economic_indicators?.cpi_latest),
      unemployment_rate: firstPresent(d.unemployment_rate, d.economic_indicators?.unemployment_rate),

      mbs_spread: firstPresent(d.mbs_spread, d.spreads?.mortgage_treasury_spread),
      mortgage_treasury_spread: firstPresent(d.mortgage_treasury_spread, d.spreads?.mortgage_treasury_spread),
      mortgage_fed_spread: firstPresent(d.mortgage_fed_spread, d.spreads?.mortgage_fed_spread),
      yield_curve_spread: firstPresent(d.yield_curve_spread, d.yield_curve?.['2yr_10yr_spread']),

      qt_pressure: firstPresent(d.qt_pressure, d.risk_indicators?.qt_pressure, d.fed_policy?.qt_pressure),
      qt_intensity: firstPresent(d.qt_intensity, d.fed_policy?.qt_intensity, d.risk_indicators?.qt_pressure),
      geopolitical_risk: firstPresent(d.geopolitical_risk, d.risk_factors?.geopolitical_risk, d.risk_indicators?.geopolitical_risk),

      fed_treasury_holdings: firstPresent(d.fed_treasury_holdings, d.fed_balance_sheet?.treasury_holdings),
      fed_mbs_holdings: firstPresent(d.fed_mbs_holdings, d.fed_balance_sheet?.mbs_holdings),
      fed_total_portfolio: firstPresent(d.fed_total_portfolio, d.fed_balance_sheet?.total_assets),
      treasury_runoff_rate: firstPresent(d.treasury_runoff_rate, d.fed_balance_sheet?.treasury_runoff_rate),
      mbs_runoff_rate: firstPresent(d.mbs_runoff_rate, d.fed_balance_sheet?.mbs_runoff_rate),
      total_runoff_rate: firstPresent(d.total_runoff_rate, d.fed_balance_sheet?.total_runoff_rate),

      inflation_expectations: firstPresent(d.inflation_expectations, d.economic_indicators?.five_year_breakeven, d.economic_indicators?.inflation_expectations),
      core_pce: firstPresent(d.core_pce, d.economic_indicators?.core_pce),
      job_openings: firstPresent(d.job_openings, d.economic_indicators?.job_openings),
      jobless_claims: firstPresent(d.jobless_claims, d.economic_indicators?.weekly_claims, d.economic_indicators?.jobless_claims),
      mbs_demand: firstPresent(d.mbs_demand, d.mortgage_market?.mbs_demand),
      gdp_growth: firstPresent(d.gdp_growth, d.economic_indicators?.gdp_growth),
      consumer_confidence: firstPresent(d.consumer_confidence, d.economic_indicators?.consumer_confidence),
      pmi_manufacturing: firstPresent(d.pmi_manufacturing, d.economic_indicators?.pmi_manufacturing),
    },
  };
};

/**
 * Owns every network call the Market Data page makes, plus the one error model
 * the whole page reads. Keeping this in a single place is what stops `error`
 * from being a string in one code path and an object in another.
 */
export function useMarketData(period) {
  const [marketData, setMarketData] = useState(null);
  const [historicalData, setHistoricalData] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(NO_ERRORS);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchMarketData = useCallback(async () => {
    setLoading(true);
    setError((prev) => ({ ...prev, marketData: null }));

    try {
      const response = await apiService.getComprehensiveMarketData();

      if (response && response.data) {
        setMarketData(flattenMarketResponse(response));
        setLastUpdated(new Date());
      } else {
        // No stand-in values. An empty panel is honest; invented rates are not.
        setMarketData(null);
        setError((prev) => ({
          ...prev,
          marketData: 'The market data service returned an unexpected response. No live values are available right now.',
        }));
      }
    } catch (err) {
      console.error('Failed to fetch market data:', err);
      setMarketData(null);
      setError((prev) => ({
        ...prev,
        marketData: `Unable to reach the market data service: ${err.message}`,
      }));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistoricalData = useCallback(async () => {
    try {
      const response = await apiService.getHistoricalComparison(getPeriod(period).days);

      // The backend answers with a "coming soon" message when FRED is unconfigured.
      if (response?.data?.message?.includes('coming soon')) {
        const reason =
          response.data.status === 'no_api_key'
            ? 'FRED API key is not configured on the server. Please set up the FRED API key to enable historical data.'
            : response.data.status === 'no_data'
              ? 'No historical data was found for the selected period. This could be due to API limits or missing data.'
              : 'Historical data is currently unavailable.';

        setHistoricalData({ status: response.data.status || 'unavailable', message: response.data.message });
        setError((prev) => ({ ...prev, historicalData: reason }));
        return;
      }

      if (response && response.data) {
        const valueKeys = Object.keys(response.data).filter(
          (key) => !['timestamp', 'period', 'data_found', 'message', 'api_errors', 'status'].includes(key)
        );
        const hasUsableData = valueKeys.some((key) => !isMissing(response.data[key]));

        if (hasUsableData) {
          setHistoricalData(response.data);
          setError((prev) => ({ ...prev, historicalData: null }));
        } else {
          setHistoricalData({});
          setError((prev) => ({ ...prev, historicalData: 'No historical data available for the selected period.' }));
        }
      } else {
        setHistoricalData({});
        setError((prev) => ({ ...prev, historicalData: 'Failed to retrieve historical data.' }));
      }
    } catch (err) {
      console.error('Failed to fetch historical data:', err);
      setHistoricalData({});
      setError((prev) => ({ ...prev, historicalData: `Error fetching historical data: ${err.message}` }));
    }
  }, [period]);

  const fetchPredictions = useCallback(async () => {
    try {
      const response = await apiService.getAIRatePredictions();
      const payload = response?.data;

      if (!payload) {
        setPredictions(null);
        setError((prev) => ({ ...prev, predictions: 'Rate predictions are unavailable - unexpected response format.' }));
        return;
      }

      // The service emits placeholder predictions when OpenAI is unreachable or
      // unconfigured. Those are not forecasts, so they are never displayed.
      const isFallback =
        payload.is_fallback === true || ['fallback', 'none', 'error'].includes(payload.model_used);

      if (isFallback || !payload.predictions || Object.keys(payload.predictions).length === 0) {
        setPredictions(null);
        setError((prev) => ({
          ...prev,
          predictions: payload.error || 'Rate predictions are not configured on the server.',
        }));
        return;
      }

      setPredictions({ byKey: payload.predictions, model: payload.model_used, timestamp: payload.timestamp });
      setError((prev) => ({ ...prev, predictions: null }));
    } catch (err) {
      console.error('Failed to fetch rate predictions:', err);
      setPredictions(null);
      setError((prev) => ({ ...prev, predictions: `Unable to load rate predictions: ${err.message}` }));
    }
  }, []);

  // Current values and predictions don't depend on the comparison period.
  useEffect(() => {
    fetchMarketData();
    fetchPredictions();

    const interval = setInterval(() => {
      fetchMarketData();
      fetchPredictions();
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [fetchMarketData, fetchPredictions]);

  // Historical values do. Refetching current values here as well produced two
  // concurrent requests racing each other on every mount.
  useEffect(() => {
    fetchHistoricalData();
    const interval = setInterval(fetchHistoricalData, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchHistoricalData]);

  const refresh = useCallback(() => {
    fetchMarketData();
    fetchHistoricalData();
    fetchPredictions();
  }, [fetchMarketData, fetchHistoricalData, fetchPredictions]);

  /** Current value vs the same field `period` ago. Returns null when unavailable. */
  const getComparison = useCallback(
    (currentValue, key) => {
      if (isMissing(currentValue)) return null;

      if (historicalData?.status && historicalData?.message) {
        return { status: historicalData.status, message: historicalData.message, period };
      }

      if (!historicalData || Object.keys(historicalData).length === 0) return null;

      let historicalValue = historicalData[key];

      if (isMissing(historicalValue)) {
        for (const alias of HISTORICAL_KEY_ALIASES[key] || []) {
          if (!isMissing(historicalData[alias])) {
            historicalValue = historicalData[alias];
            break;
          }
        }
      }

      if (isMissing(historicalValue)) return null;

      const difference = Number(currentValue) - Number(historicalValue);

      return {
        historical: Number(historicalValue),
        difference,
        // A zero baseline makes percent change meaningless, not Infinity.
        percentChange: Number(historicalValue) === 0 ? null : (difference / Number(historicalValue)) * 100,
        trend: difference > 0 ? 'up' : difference < 0 ? 'down' : 'flat',
        period,
      };
    },
    [historicalData, period]
  );

  /** Prediction for a field, or null. Never a placeholder. */
  const getPrediction = useCallback(
    (key) => {
      const byKey = predictions?.byKey;
      if (!byKey) return null;

      const match = byKey[key] || PREDICTION_KEY_ALIASES[key]?.map((alias) => byKey[alias]).find(Boolean);
      if (!match || isMissing(match.value)) return null;

      return {
        value: match.value,
        trend: match.trend || null,
        timeframe: match.timeframe || null,
        rationale: match.details || match.rationale || null,
        model: predictions.model,
      };
    },
    [predictions]
  );

  return {
    marketData,
    historicalData,
    predictions,
    loading,
    error,
    // Predictions are an optional add-on, so their absence must not light up the
    // page-level error banner. They report inline instead.
    hasError: Boolean(error.marketData || error.historicalData),
    lastUpdated,
    refresh,
    getComparison,
    getPrediction,
  };
}
